import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  FeatureFlagsProvider,
  LoaderWidget,
  RootProvider,
  ServicesProvider,
  useFeatureFlagsContext,
  useRootContext,
  useServicesContext,
} from '@jarvis/react-portal-addons';
import { ToastProvider } from '@veneer/core';
import resources from './assets/locale/index';
import GenericThemeProvider from './shared/GenericThemeProvider';
import DashboardComponent from './components/Dashboard/index';
import AdminConsolidatedDashboard from './components/ConsolidatedDashboard/AdminDashboard';
import EndUserConsolidatedDashboard from './components/ConsolidatedDashboard/EndUserDashboard';
import TouModal from './components/ToU/Modal';
import WelcomeTouInlineNotification from './components/WelcomeTouInlineNotification';
import {
  HP_APP_TOU_DOCUMENT_PATH,
  HP_SUPPORT_HOSTNAME,
  ONBOARDING_CENTER_STACK_HOSTNAMES,
  USER_ROLES,
} from './utils/constants';
import { getCurrentTouUserKey, hasTouConsent, setTouConsent } from './utils/touLocalStorage';
import {
  hasDismissedWelcomeTouInlineNotification,
  setWelcomeTouInlineNotificationDismissed,
} from './utils/welcomeTouInlineNotificationLocalStorage';

const App = props => {
  const { shell } = props;
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [currentUserKey, setCurrentUserKey] = useState(null);
  const [isResolvingTouUser, setIsResolvingTouUser] = useState(true);
  const [hasTouConsentForUser, setHasTouConsentForUser] = useState(false);
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);
  const {
    consolidatedPortalDashbordLayout,
    consolidatedPortalDashboardEndUser,
    dashboardShowTouModal,
    dashboardShowTouInlineNotification,
  } = useFeatureFlagsContext();
  const { pending: servicesPending } = useServicesContext();
  const {
    shell: {
      localization,
      criterion,
      navigation,
      authProvider: rootAuthProvider,
      sessionInterface: rootSessionInterface,
    },
    stack,
    properties,
  } = useRootContext();
  const locale = localization?.locale;
  const {
    authProvider: shellAuthProvider,
    sessionInterface: shellSessionInterface,
  } = shell ?? {};
  const authProvider = shellAuthProvider ?? rootAuthProvider;
  const sessionInterface = shellSessionInterface ?? rootSessionInterface;

  const {
    criterionKeys,
    utoRedirect,
  } = properties ?? {};
  const isAdminCriterionKey = criterionKeys?.isAdminCriterionKey ?? '';

  const getSoftwareURL = useCallback(() => {
    const [language, country] = locale.split('-');
    const hostname = ONBOARDING_CENTER_STACK_HOSTNAMES[stack];

    return `${hostname}/${country}/${language}/get-software`.toLowerCase();
  }, [locale, stack]);

  const getControlHubTouUrl = useCallback(() => {
    const [language, country] = locale.split('-');

    return `/${country}/${language}/tou`.toLowerCase();
  }, [locale]);

  const getHpAppTouUrl = useCallback(() => {
    const [language, country] = locale.split('-');

    return `${HP_SUPPORT_HOSTNAME}/${country}-${language}/${HP_APP_TOU_DOCUMENT_PATH}`.toLowerCase();
  }, [locale]);

  useEffect(() => {
    const handleInviteFlow = async () => {
      const inviteParam = new URLSearchParams(globalThis.location.search).get('invite');
      const isEndUser = !(await criterion.checkByCriterionKey(isAdminCriterionKey));

      setIsCheckingRole(false);
      setUserRole(isEndUser ? USER_ROLES.END_USER : USER_ROLES.ADMIN);

      if (!utoRedirect && inviteParam === 'true' && isEndUser && stack !== undefined) {
        globalThis.location.assign(getSoftwareURL());
      }
    };

    handleInviteFlow();
  }, [criterion, getSoftwareURL, locale, stack, isAdminCriterionKey, utoRedirect]);

  useEffect(() => {
    let isMounted = true;

    const resolveCurrentTouUserKey = async () => {
      setIsResolvingTouUser(true);

      let resolvedUserKey = null;

      try {
        resolvedUserKey = await getCurrentTouUserKey({ authProvider, stack });
      } catch (error) {
        resolvedUserKey = null;
      }

      if (!isMounted) {
        return;
      }

      setCurrentUserKey(resolvedUserKey);
      setHasTouConsentForUser(hasTouConsent(resolvedUserKey));
      setShowWelcomeNotification(!hasDismissedWelcomeTouInlineNotification(resolvedUserKey));
      setIsResolvingTouUser(false);
    };

    resolveCurrentTouUserKey();

    return () => {
      isMounted = false;
    };
  }, [authProvider, stack]);

  const handleTouAccept = useCallback(() => {
    const didPersistConsent = setTouConsent(currentUserKey);
    setHasTouConsentForUser(didPersistConsent || !currentUserKey);
  }, [currentUserKey]);

  const handleTouExit = useCallback(async () => {
    await sessionInterface?.logout?.();
  }, [sessionInterface]);

  const handleWelcomeNotificationClose = useCallback(() => {
    setWelcomeTouInlineNotificationDismissed(currentUserKey);
    setShowWelcomeNotification(false);
  }, [currentUserKey]);

  const shouldShowTouModal = Boolean(
    dashboardShowTouModal
    && !isResolvingTouUser
    && !hasTouConsentForUser,
  );

  const renderTouGate = useCallback(() => (
    <GenericThemeProvider>
      <TouModal
        show={shouldShowTouModal}
        onAccept={handleTouAccept}
        onExit={handleTouExit}
        touUrl="/tou"
      />
    </GenericThemeProvider>
  ), [handleTouAccept, handleTouExit, shouldShowTouModal]);

  if (utoRedirect) {
    const shellPostLoginRedirect = new URLSearchParams(window.location.search);

    const postUtoRedirect = new URLSearchParams(window.location.search);
    postUtoRedirect.set('postLoginRedirect', '/home?invite=true');

    // Set Shell's postLoginRedirect param so that after login in via Shell,
    // it redirects the user back to the /uto route to complete the invite user flow.
    shellPostLoginRedirect.set('postLoginRedirect', `/uto?${postUtoRedirect.toString()}`);

    // Redirects the user to the /uto route to start the invite user flow.
    // Pass all the existing query params to the /uto route.
    navigation.push(`/uto?${shellPostLoginRedirect.toString()}`);

    return <LoaderWidget fullScreen />;
  }

  const showLoader = (servicesPending || isCheckingRole) && consolidatedPortalDashbordLayout;

  if (showLoader) {
    return <LoaderWidget fullScreen />;
  }

  if (shouldShowTouModal) {
    return renderTouGate();
  }

  const welcomeTouNotification = dashboardShowTouInlineNotification && showWelcomeNotification && (
    <WelcomeTouInlineNotification
      hpAppTouUrl={getHpAppTouUrl()}
      onClose={handleWelcomeNotificationClose}
      touUrl={getControlHubTouUrl()}
    />
  );

  if (!consolidatedPortalDashbordLayout) {
    return (
      <>
        {welcomeTouNotification}
        <DashboardComponent {...props} />
      </>
    );
  }

  if (userRole === USER_ROLES.END_USER && consolidatedPortalDashboardEndUser) {
    return (
      <>
        {welcomeTouNotification}
        <EndUserConsolidatedDashboard />
      </>
    );
  }

  return (
    <>
      {welcomeTouNotification}
      <AdminConsolidatedDashboard />
    </>
  );
};

const ServicesProviderWrapper = ({ children, shell, stack }) => (
  <ServicesProvider
    shell={shell}
    stack={stack}
  >
    {children}
  </ServicesProvider>
);

ServicesProviderWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  shell: PropTypes.objectOf(PropTypes.any).isRequired,
  stack: PropTypes.number.isRequired,
};

const Dashboard = props => {
  const { shell, stack, properties } = props;

  const localization = shell?.localization || {};

  const flags = useMemo(
    () => [
      { key: 'consolidatedPortalCheckAdvancedViewGrant' },
      { key: 'consolidatedPortalDashboardEndUser' },
      { key: 'consolidatedPortalDashbordLayout' },
      { key: 'consolidatedPortalGetAppButton' },
      { key: 'consolidatedPortalGetHpAppCard' },
      { key: 'consolidatedPortalHpAllInCard' },
      { key: 'consolidatedPortalInstantInkCard' },
      { key: 'consolidatedPortalPrinterCarousel' },
      { key: 'consolidatedPortalSustainabilityCard' },
      { key: 'consolidatedPortalUsageDataTelemetryCheck' },
      { key: 'dashboardDetailUsageModalPrinterClick' },
      { key: 'dashboardHideColumnReorder' },
      { key: 'dashboardHideSegmentedControl' },
      { key: 'dashboardPrintAnywhereV2' },
      { key: 'dashboardShowFiltersDetailedUsage' },
      { key: 'dashboardShowNoDataYet' },
      { key: 'dashboardShowTouInlineNotification' },
      { key: 'dashboardShowTouModal' },
      { key: 'dashboardUsageDataUnitFromOrg' },
      { key: 'grantsSupport' },
      { key: 'printerSecurityRename' },
      { key: 'renamePortal' },
      { key: 'smartSecurityRemoval' },
    ],
    [],
  );

  return (
    <ToastProvider timeout={5}>
      <FeatureFlagsProvider
        clientKey="smb-portal"
        flags={flags}
      >
        <RootProvider
          id="react-smb-dashboard"
          stack={stack}
          shell={shell}
          localization={localization}
          resources={resources}
          properties={properties}
        >
          <ServicesProviderWrapper
            shell={shell}
            stack={stack}
          >
            <App {...props} />
          </ServicesProviderWrapper>
        </RootProvider>
      </FeatureFlagsProvider>
    </ToastProvider>
  );
};

Dashboard.defaultProps = {
  shell: {},
  properties: {},
};

Dashboard.propTypes = {
  shell: PropTypes.objectOf(PropTypes.any),
  stack: PropTypes.number.isRequired,
  properties: PropTypes.objectOf(PropTypes.any),
};

App.propTypes = {
  shell: PropTypes.objectOf(PropTypes.any).isRequired,
  stack: PropTypes.number.isRequired,
};

export default Dashboard;
